// ============================================================
// NON-PAID FORECAST ENGINE
//
// Computes revenue forecasts for non-paid (organic) sources.
//
// PIPELINE (sequential stages):
//   ┌──────────────────────────────────────────────────────────┐
//   │ STAGE 1: HISTORICAL DATA EXTRACTION                     │
//   │   • Parse revenue transactions → daily revenue per      │
//   │     non-paid source                                     │
//   │   • Compute global daily total (for Bayesian prior)     │
//   ├──────────────────────────────────────────────────────────┤
//   │ STAGE 2: PER-SOURCE FORECAST GENERATION                 │
//   │   • Build zero-padded daily series                      │
//   │   • Recency check (days since last sale)                │
//   │   • Match source to evolution model                     │
//   │   • Bayesian prior + DES smoothing                      │
//   │   • Recency decay for stale sources                     │
//   │   • Day-of-week seasonality (detrended)                 │
//   │   • Adaptive jitter profiling                           │
//   │   • Generate forecast with:                             │
//   │       - Damped trend                                    │
//   │       - Seasonality                                     │
//   │       - Evolution function                              │
//   │       - Impulse or Gaussian noise                       │
//   │   • Backtesting (withhold 7 days)                       │
//   │   • Build chart-ready data array                        │
//   ├──────────────────────────────────────────────────────────┤
//   │ STAGE 3: SORT & RETURN                                  │
//   │   • Sort sources by total historical revenue (desc)     │
//   └──────────────────────────────────────────────────────────┘
//
// ============================================================

import { format, parseISO, addDays } from 'date-fns';

import {
    NonPaidPoint,
    NonPaidForecastResult,
    NonPaidSourceForecast,
    JitterProfile,
} from './types';
import { getEvolutionConfig } from './constants';
import { seededRandom, normalRandom } from './math';
import { computeAdaptiveJitter } from './adaptiveJitter';

// ============================================================
// PUBLIC API: computeNonPaidForecast
// ============================================================

/**
 * Compute revenue forecasts for all non-paid (organic) traffic sources.
 *
 * @param finance       The finance data object (contains transactions)
 * @param forecastDays  Number of days to forecast (7, 30, or 90)
 * @param directOrganicLabel  Translated label for "Direct / Organic" fallback
 * @returns             NonPaidForecastResult with per-source charts + jitter profiles
 */
export const computeNonPaidForecast = (
    finance: any,
    forecastDays: number,
    directOrganicLabel: string
): NonPaidForecastResult => {

    const txs = finance?.transactions?.filter((tx: any) => tx.isActive !== false && tx.type === 'REVENUE') || [];
    if (txs.length < 7) return { charts: [] as NonPaidSourceForecast[], npJitterProfiles: {} };

    // ============================================================
    // STAGE 1: HISTORICAL DATA EXTRACTION
    // ============================================================

    // 1a. Group daily revenue by non-paid source
    const sourceDaily: Record<string, Record<string, number>> = {};
    const allDates = new Set<string>();

    txs.forEach((tx: any) => {
        const dateKey = tx.date?.split('T')[0];
        if (!dateKey) return;

        allDates.add(dateKey);

        const traffic = tx.traffic ? (typeof tx.traffic === 'string' ? JSON.parse(tx.traffic) : tx.traffic) : {};
        const isPaid = traffic.isPaid === true;
        if (isPaid) return; // Skip paid sources

        // Match identification algorithm EXACTLY as in TrafficPage
        const sourceName = traffic.target || directOrganicLabel;

        if (!sourceDaily[sourceName]) sourceDaily[sourceName] = {};
        sourceDaily[sourceName][dateKey] = (sourceDaily[sourceName][dateKey] || 0) + (Number(tx.amount) || 0);
    });

    const sortedDates = Array.from(allDates).sort();
    if (sortedDates.length < 2) return { charts: [] as NonPaidSourceForecast[], npJitterProfiles: {} };

    // 1b. Calculate Global Prior (Average daily revenue across ALL non-paid sources)
    //     Used for Bayesian shrinkage when a source has few data points
    const globalDailyTotal: Record<string, number> = {};
    sortedDates.forEach(d => {
        globalDailyTotal[d] = 0;
        Object.values(sourceDaily).forEach(src => { globalDailyTotal[d] += src[d] || 0; });
    });
    const globalMathSeries = sortedDates.map(d => globalDailyTotal[d]);
    const globalMean = globalMathSeries.reduce((a, b) => a + b, 0) / Math.max(1, globalMathSeries.length);


    // ============================================================
    // STAGE 2: PER-SOURCE FORECAST GENERATION
    // ============================================================

    // Jitter profiles for non-paid sources
    const npJitterProfiles: Record<string, JitterProfile> = {};

    const charts: NonPaidSourceForecast[] = [];

    Object.entries(sourceDaily).forEach(([sourceName, daily]) => {

        // 2a. Build zero-padded series
        //     fullSeries: null-safe (can contain 0)
        //     mathSeries: always numeric (0 for missing days)
        const fullSeries: (number | null)[] = sortedDates.map((d: string) => daily[d] ?? 0);
        const mathSeries: number[] = sortedDates.map((d: string) => daily[d] || 0);
        const validCount = mathSeries.filter((v: number) => v > 0).length;
        if (validCount === 0) return;

        // 2b. Recency Check: How long since the last sale?
        let lastSaleIdx = -1;
        for (let i = mathSeries.length - 1; i >= 0; i--) { if (mathSeries[i] > 0) { lastSaleIdx = i; break; } }
        const daysSinceLastSale = mathSeries.length - 1 - lastSaleIdx;

        // 2c. Get evolution model for this source type
        const evoConfig = getEvolutionConfig(sourceName);

        // 2d. ECOMMERCE-GRADE INITIALIZATION
        //     Trust factor grows with the number of sales (reaches 1.0 at 10 sales)
        const trustFactor = Math.min(1, validCount / 10);
        const sourceMean = mathSeries.reduce((a, b) => a + b, 0) / mathSeries.length;

        // For very sparse sources (< 5 sales), skip Bayesian prior entirely.
        // The source's own average IS the best estimate. Don't inflate.
        let priorLevel: number;
        if (validCount < 5) {
            // ECOMMERCE: A product with 3 sales doesn't need a global prior.
            // Its daily average is the forecast. Period.
            priorLevel = sourceMean;
        } else {
            // CAP the global prior: at most 50% above the source's daily average.
            const numSources = Math.max(1, Object.keys(sourceDaily).length);
            const globalPriorPerSource = globalMean / numSources;
            const cappedPrior = Math.min(globalPriorPerSource, Math.max(sourceMean * 1.5, 0.5));
            priorLevel = (trustFactor * sourceMean) + ((1 - trustFactor) * cappedPrior);
        }

        // 2e. INERTIA GUARDRAIL: Only allow growth trends if we have > 7 data points
        const alpha = trustFactor > 0.5 ? 0.15 : 0.10; // Slightly higher alpha for faster convergence
        const beta = trustFactor > 0.7 ? 0.05 : 0; // Force trend to 0 for low signal

        let level = priorLevel, trend = 0;

        // Seed trend only for high-signal sources (>= 7 valid data points)
        if (validCount >= 7 && mathSeries.length >= 2) {
            const window = mathSeries.slice(-14);
            const n = window.length;
            let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
            for (let k = 0; k < n; k++) { sumX += k; sumY += window[k]; sumXY += k * window[k]; sumXX += k * k; }
            const denom = n * sumXX - sumX * sumX;
            trend = denom !== 0 ? (n * sumXY - sumX * sumY) / denom : 0;
        }

        // 2f. Run smoothed DES
        for (let k = 1; k < mathSeries.length; k++) {
            const prev = level;
            level = alpha * mathSeries[k] + (1 - alpha) * (level + trend);
            trend = beta * (level - prev) + (1 - beta) * trend;
        }

        // 2g. RECENCY DECAY: If source is "stale" (>14 days), decay the starting level
        if (daysSinceLastSale > 14) {
            const decayDays = daysSinceLastSale - 14;
            level *= Math.exp(-0.05 * decayDays); // 5% daily decay of "momentum"
            trend *= 0.5; // Zap the trend
        }

        // 2h. Day-of-week seasonality (detrended)
        const recentWindow = Math.min(28, sortedDates.length);
        const recentSlice = fullSeries.slice(-recentWindow);
        let localSum = 0, localCnt = 0;
        recentSlice.forEach((v: number | null) => { if (v !== null) { localSum += v; localCnt++; } });
        const locallyScopedMean = localCnt > 0 ? localSum / localCnt : 1;
        let smoothLvl = locallyScopedMean;
        const localLevels: number[] = [];
        recentSlice.forEach((v: number | null) => {
            if (v !== null) smoothLvl = 0.3 * v + 0.7 * smoothLvl;
            localLevels.push(smoothLvl);
        });

        const dowEffect = new Array(7).fill(0), dowCount = new Array(7).fill(0);
        recentSlice.forEach((val: number | null, i: number) => {
            if (val === null || localLevels[i] <= 0) return;
            const di = sortedDates.length - recentWindow + i;
            if (di < 0 || di >= sortedDates.length) return;
            const dow = new Date(sortedDates[di]).getDay();
            dowEffect[dow] += val / localLevels[i];
            dowCount[dow]++;
        });
        for (let d = 0; d < 7; d++) dowEffect[d] = dowCount[d] > 0 ? dowEffect[d] / dowCount[d] : 1;

        // 2i. ADAPTIVE JITTER for this non-paid source
        const npJitterProfile = computeAdaptiveJitter(
            mathSeries, mathSeries.filter(v => v > 0), sortedDates.length,
            daysSinceLastSale, undefined, true
        );
        npJitterProfiles[`np-${sourceName}`] = npJitterProfile;

        const npCV = npJitterProfile.relativeCV;
        const npNoiseMult = npJitterProfile.noiseMultiplier;
        const npBandMult = npJitterProfile.bandWidthMultiplier;

        const lastDateObj = new Date(sortedDates[sortedDates.length - 1]);

        // 2j. Generate forecast
        let phi = 0.95;
        const forecastResult: { baseline: number; noise: number; upper: number; lower: number }[] = [];

        // ---- ECOMMERCE EXPERIENCE: DAMPED MOMENTUM ----
        // Only allow positive trend for sources with >= 7 valid data points.
        // Sparse sources (3 sales in a month) do NOT have enough signal for trend.
        if (validCount >= 7 && trend > 0) {
            phi = 0.70;
        } else {
            trend = 0;
        }

        // ---- FORECAST CAP ----
        // ECOMMERCE: A $26/week product should NEVER forecast $40+ in a single day.
        const npHistoricalMax = Math.max(...mathSeries.filter(v => v > 0), 1);
        const npForecastCap = validCount < 7 ? npHistoricalMax * 1.2 : npHistoricalMax * 2.0;

        // Noise damping for sparse data: don't create wild swings from tiny baselines
        const npSparseDamper = validCount < 5 ? 0.3 : validCount < 10 ? 0.6 : 1.0;

        for (let i = 0; i < forecastDays; i++) {
            const futureDate = addDays(lastDateObj, i + 1);

            const dow = futureDate.getDay();
            let dampedCum = 0;
            for (let j = 1; j <= i + 1; j++) dampedCum += Math.pow(phi, j) * trend;
            let base = level + dampedCum;

            // Apply day-of-week seasonality (only with enough data to trust it)
            const seasonConf = validCount >= 14 ? 1.0 : validCount >= 7 ? 0.5 : 0.0;
            if (seasonConf > 0) {
                base *= 1 + (dowEffect[dow] - 1) * seasonConf;
            }

            // KEY: Apply evolution function as moderator
            base *= evoConfig.fn(i);

            // Apply forecast cap: never exceed conservative multiple of historical max
            base = Math.min(base, npForecastCap);

            // MULTIPLICATIVE NOISE: amplitude is exponentially tied to the trend value.
            // noise ∝ base * CV — shrinks near zero, grows with the trend.
            const safeBase = Math.max(0, base);
            const noiseSeed = seededRandom(`${sourceName}-np-${i}`);
            let noiseSample = 0;

            if (npJitterProfile.useImpulseNoise) {
                if (noiseSeed > (1 - npJitterProfile.impulseProbability)) {
                    noiseSample = (noiseSeed - (1 - npJitterProfile.impulseProbability)) * safeBase * npCV * (10 / npJitterProfile.impulseProbability) * npNoiseMult * npSparseDamper;
                }
            } else {
                noiseSample = normalRandom(`${sourceName}-np-${i}`) * safeBase * npCV * npNoiseMult * npSparseDamper;
            }

            // Clamp noise: for sparse data, max ±20% of base
            const npMaxNoiseRatio = validCount < 5 ? 0.20 : validCount < 10 ? 0.35 : 0.50;
            noiseSample = Math.max(-npMaxNoiseRatio * safeBase, Math.min(npMaxNoiseRatio * safeBase, noiseSample));

            const margin = safeBase * npCV * npBandMult * npSparseDamper * Math.sqrt(1 + 0.08 * i);

            forecastResult.push({
                baseline: safeBase,
                noise: noiseSample,
                upper: Math.max(0, safeBase + margin),
                lower: Math.max(0, safeBase - margin)
            });
        }

        // 2k. BACKTESTING (withhold last 7 days, zero-aware)
        let backtestScore: number | null = null;
        if (mathSeries.length >= 4) {
            const btHoldout = 7;
            const btTrain = mathSeries.slice(0, -btHoldout);
            const btActual = mathSeries.slice(-btHoldout);

            let btLevel = btTrain[0], btTrend = 0;
            if (btTrain.length > 7) {
                const w = btTrain.slice(-14);
                const n = w.length;
                let sx = 0, sy = 0, sxy = 0, sxx = 0;
                for (let k = 0; k < n; k++) { sx += k; sy += w[k]; sxy += k * w[k]; sxx += k * k; }
                const den = n * sxx - sx * sx;
                btTrend = den !== 0 ? (n * sxy - sx * sy) / den : 0;
            }
            for (let k = 1; k < btTrain.length; k++) {
                const pr = btLevel;
                btLevel = alpha * btTrain[k] + (1 - alpha) * (btLevel + btTrend);
                btTrend = beta * (btLevel - pr) + (1 - beta) * btTrend;
            }

            let totalError = 0, count = 0;
            for (let i = 0; i < btHoldout; i++) {
                let dampCum = 0;
                for (let j = 1; j <= i + 1; j++) dampCum += Math.pow(phi, j) * btTrend;
                const pred = Math.max(0, (btLevel + dampCum) * evoConfig.fn(i));
                const actualValue = btActual[i];

                // SMAPE variant to handle zeros safely
                const error = Math.abs(pred - actualValue) / (Math.max(1, (pred + actualValue) / 2));
                totalError += error;
                count++;
            }
            if (count > 0) {
                const calculatedScore = (1 - (totalError / count) / 2) * 100;
                backtestScore = isNaN(calculatedScore) ? null : Math.max(0, Math.min(100, calculatedScore));
            }
        }

        // 2l. BUILD CHART DATA — Combine historical display window + forecast
        const displayDates = sortedDates.slice(-Math.min(30, sortedDates.length));
        const offset = sortedDates.length - displayDates.length;
        const chartData: NonPaidPoint[] = [];

        // Historical points
        displayDates.forEach((date, di) => {
            const idx = offset + di;
            const val = fullSeries[idx];
            const isLast = di === displayDates.length - 1;
            chartData.push({
                label: format(parseISO(date), 'MMM d'),
                date: date,
                actual: val,
                baseline: isLast ? val : null,
                stochasticNoise: 0,
                upper: isLast ? val : null,
                lower: isLast ? val : null
            });
        });

        // Forecast points
        for (let i = 0; i < forecastDays; i++) {
            const futureDate = addDays(lastDateObj, i + 1);
            chartData.push({
                label: format(futureDate, 'MMM d'),
                date: futureDate.toISOString().split('T')[0],
                actual: null,
                baseline: forecastResult[i].baseline,
                stochasticNoise: forecastResult[i].noise,
                upper: forecastResult[i].upper,
                lower: forecastResult[i].lower
            });
        }

        charts.push({
            source: sourceName,
            color: evoConfig.color,
            evolutionLabel: evoConfig.label,
            data: chartData,
            backtestScore,
            jitterProfile: npJitterProfile
        });
    });

    // ============================================================
    // STAGE 3: SORT & RETURN
    // ============================================================

    // Sort by total historical revenue (desc)
    charts.sort((a, b) => {
        const sumA = a.data.reduce((s, p) => s + (p.actual || 0), 0);
        const sumB = b.data.reduce((s, p) => s + (p.actual || 0), 0);
        return sumB - sumA;
    });

    return { charts, npJitterProfiles };
};
